import { useState, useEffect, useRef } from 'react';
import Pusher from 'pusher-js';
import axios from 'axios';
import './Chat.css';

const API_URL = process.env.NODE_ENV === 'development' 
  ? process.env.REACT_APP_API_URL || 'http://localhost:3001'
  : window.location.origin;

const Chat = ({ streamId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [username, setUsername] = useState('');
  const [showUsernamePopup, setShowUsernamePopup] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load chat history
  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/chat/${streamId}`);
        setMessages(response.data);
        setIsLoading(false);
        scrollToBottom();
      } catch (err) {
        console.error('Error loading chat history:', err);
        setError('Failed to load chat history');
        setIsLoading(false);
      }
    };

    loadChatHistory();
  }, [streamId]);

  useEffect(() => {
    const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'us2',
      encrypted: true
    });

    const channel = pusher.subscribe(`stream-${streamId}`);

    channel.bind('chat-message', (data) => {
      setMessages(prev => [...prev, data]);
      scrollToBottom();
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
      pusher.disconnect();
    };
  }, [streamId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInputFocus = () => {
    if (!username) {
      setShowUsernamePopup(true);
      inputRef.current?.blur();
    }
  };

  const handleInputClick = () => {
    if (!username) {
      setShowUsernamePopup(true);
      inputRef.current?.blur();
    }
  };

  const handleUsernameSubmit = (e) => {
    e.preventDefault();
    if (username.trim()) {
      setShowUsernamePopup(false);
      const systemMessage = {
        type: 'system',
        content: `Welcome to the chat, ${username}!`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, systemMessage]);
      inputRef.current?.focus();
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API_URL}/api/chat`, {
        streamId,
        type: 'user',
        content: newMessage,
        username,
        timestamp: new Date().toISOString()
      });

      setNewMessage('');
      setError(null);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>Live Chat</h3>
      </div>

      <div className="messages-container">
        {isLoading ? (
          <div className="loading-messages">Loading chat history...</div>
        ) : (
          <>
            {messages.map((msg, index) => (
              <div 
                key={index}
                className={`message ${msg.type === 'system' ? 'system-message' : ''}`}
              >
                {msg.type !== 'system' && (
                  <span className="message-username">{msg.username}</span>
                )}
                <span className="message-content">{msg.content}</span>
                <span className="message-time">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="chat-input">
        <input
          ref={inputRef}
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          placeholder={username ? "Type a message..." : "Click to join chat..."}
          maxLength={500}
        />
        <button type="submit" disabled={!username || !newMessage.trim()}>
          Send
        </button>
      </form>

      {showUsernamePopup && (
        <>
          <div className="modal-overlay" onClick={() => setShowUsernamePopup(false)} />
          <div className="username-modal">
            <h3>Enter Username</h3>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              autoFocus
              required
            />
            <button onClick={handleUsernameSubmit}>Join Chat</button>
          </div>
        </>
      )}

      {error && <div className="chat-error">{error}</div>}
    </div>
  );
};

export default Chat; 